import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Text } from '@/components/ui/Text'
import { CreditIcon } from '@/components/icons/CreditIcon'
import {
  SOCIAL_LISTENING_CREDIT_RATE,
  creditRateRows,
} from '@/lib/billing/creditRateTable'
import { formatCreditAmount } from '@/lib/billing/formatCreditAmount'
import { useMeteringEnrolled } from '@/lib/billing/metering'
import { useTranslation } from 'react-i18next'
import { MeteredPricingTables } from './MeteredPricingTables'

const CREDIT_RATE_KEYS: Record<string, { action: string; unit: string }> = {
  'Audience creation': {
    action: 'billing.plans.rates.audienceCreation',
    unit: 'billing.plans.rates.perTwin',
  },
  Study: {
    action: 'billing.plans.rates.study',
    unit: 'billing.plans.rates.perRespondent',
  },
  'Quick study': {
    action: 'billing.plans.rates.quickStudy',
    unit: 'billing.plans.rates.perRespondentPerQuestion',
  },
  Chat: {
    action: 'billing.plans.rates.chat',
    unit: 'billing.plans.rates.perMessage',
  },
  'Focus group': {
    action: 'billing.plans.rates.focusGroup',
    unit: 'billing.plans.rates.perMessage',
  },
  Image: {
    action: 'billing.plans.rates.image',
    unit: 'billing.plans.rates.perImage',
  },
  'Assistant weighted': {
    action: 'billing.plans.rates.assistantWeighted',
    unit: 'billing.plans.rates.perMillionWeightedTokens',
  },
  'Assistant typical': {
    action: 'billing.plans.rates.assistantTypical',
    unit: 'billing.plans.rates.perMessage',
  },
  'Chat levels': {
    action: 'billing.plans.rates.chatLevels',
    unit: 'billing.plans.rates.perMessage',
  },
  'Focus group levels': {
    action: 'billing.plans.rates.focusGroupLevels',
    unit: 'billing.plans.rates.perMessage',
  },
  'Social listening': {
    action: 'billing.plans.rates.socialListening',
    unit: 'billing.plans.rates.per150PostsAdded',
  },
}

export interface HowCreditsWorkDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Credit metering enrollment; defaults to the signed-in account's own mode. */
  enrolled?: boolean
}

/**
 * Standard private-audience rates, plus social listening's rate per posts
 * added. Audience-specific overrides stay off this table: a custom rate
 * belongs to one audience, and this dialog is the default card every customer
 * sees.
 */
export function HowCreditsWorkDialog(props: HowCreditsWorkDialogProps) {
  const { open, onOpenChange, enrolled: enrolledProp } = props
  const { t } = useTranslation('app')
  const accountEnrolled = useMeteringEnrolled()
  const isEnrolled = enrolledProp ?? accountEnrolled
  const creditRateRowsShown = [...creditRateRows(isEnrolled), SOCIAL_LISTENING_CREDIT_RATE]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] max-w-3xl gap-0 overflow-y-auto p-0 sm:p-0"
        data-testid="how-credits-work-dialog"
      >
        <div className="flex flex-col gap-4 px-5 pb-6 pt-5 sm:px-6 sm:pb-6">
          <DialogHeader className="space-y-2 text-left sm:text-left">
            <DialogTitle className="text-2xl font-normal tracking-tight">
              {t('billing.plans.howCreditsWork')}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
              {t('billing.plans.howCreditsBody')}
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-foreground sm:w-[1%] sm:whitespace-nowrap">
                    {t('billing.plans.action')}
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-foreground">
                    {t('billing.plans.credits')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {creditRateRowsShown.map((row) => {
                  const keys = CREDIT_RATE_KEYS[row.action]
                  return (
                    <tr
                      key={row.action}
                      className="border-b border-border last:border-0"
                      data-testid={`how-credits-work-rate-${row.action.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <td className="px-4 py-2 text-sm text-foreground sm:whitespace-nowrap">
                        {keys ? t(keys.action as 'billing.plans.rates.study') : row.action}
                      </td>
                      <td className="px-4 py-2">
                        <span className="mr-1.5 inline-flex items-center gap-1 tabular-nums text-sm text-foreground">
                          <CreditIcon className="h-3 w-3 text-muted-foreground" />
                          {row.variants ? row.variants.map((variant) => formatCreditAmount(variant.rate)).join(' / ') : row.rate}
                        </span>
                        <span className="text-xs font-normal text-muted-foreground">
                          {keys ? t(keys.unit as 'billing.plans.rates.perTwin') : row.unit}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {isEnrolled ? <MeteredPricingTables /> : null}

          <Text size="xs" color="muted" className="leading-relaxed">
            {t('billing.plans.marketplaceFootnote')}
          </Text>
        </div>
      </DialogContent>
    </Dialog>
  )
}
